import { useState } from "react";
import { Download, Loader2, ShieldAlert, Trash2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { ROTULO_DO_TIPO_LGPD } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { PedidoLgpdDialog } from "./PedidoLgpdDialog";
import { AcessoDoSuporte } from "./AcessoDoSuporte";

const STATUS: Record<string, string> = { PENDENTE: "Em análise", CONCLUIDA: "Atendido", RECUSADA: "Recusado", CANCELADA: "Cancelado" };
const botao =
  "inline-flex h-9 items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-3 text-[12.5px] font-semibold text-zinc-700 transition-colors hover:bg-zinc-50 disabled:opacity-50 dark:border-border dark:bg-card dark:text-foreground dark:hover:bg-muted";

/**
 * Minha conta › Privacidade e dados (LGPD): baixar os próprios dados na hora; pedir reset ou
 * exclusão — quem executa é o suporte do DentalBook, a partir deste pedido.
 */
export function PrivacidadeSecao() {
  const utils = trpc.useUtils();
  const resumo = trpc.privacidade.resumo.useQuery();
  const [pedido, setPedido] = useState<"RESET_DADOS" | "EXCLUSAO" | null>(null);
  const recarregar = () => utils.privacidade.resumo.invalidate();

  const exportar = trpc.privacidade.exportar.useMutation({
    onSuccess: (dados) => {
      const blob = new Blob([JSON.stringify(dados, null, 2)], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `meus-dados-dentalbook-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Arquivo com seus dados baixado.");
      recarregar();
    },
    onError: (e) => toast.error(e.message || "Não foi possível gerar o arquivo."),
  });
  const cancelar = trpc.privacidade.cancelar.useMutation({
    onSuccess: () => {
      toast.success("Pedido cancelado.");
      recarregar();
    },
    onError: (e) => toast.error(e.message),
  });

  if (resumo.isLoading || !resumo.data) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-brand-600" />
      </div>
    );
  }
  const { solicitacoes, suporteLiberadoAte, acessosDoSuporte } = resumo.data;
  const pendente = (tipo: string) => solicitacoes.some((s) => s.tipo === tipo && s.status === "PENDENTE");

  return (
    <div className="space-y-6 px-6 py-5">
      <Bloco titulo="Baixar meus dados" texto="Um arquivo com seus dados de cadastro, e-mails, vínculos com organizações e preferências.">
        <button type="button" className={botao} disabled={exportar.isPending} onClick={() => exportar.mutate()}>
          {exportar.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
          Baixar meus dados
        </button>
      </Bloco>

      <AcessoDoSuporte liberadoAte={suporteLiberadoAte} acessos={acessosDoSuporte} botao={botao} />

      <Bloco
        titulo="Resetar os dados"
        texto="Apaga pacientes, agenda, pedidos, arquivos e estoque das organizações de que você é dona. A conta, as organizações, a equipe e as configurações ficam."
      >
        <button type="button" className={botao} disabled={pendente("RESET_DADOS")} onClick={() => setPedido("RESET_DADOS")}>
          <RotateCcw className="h-3.5 w-3.5" />
          {pendente("RESET_DADOS") ? "Pedido em análise" : "Pedir reset dos dados"}
        </button>
      </Bloco>

      <Bloco titulo="Excluir minha conta" texto="Apaga a conta e os dados das organizações de que você é dona. Não dá pra desfazer.">
        <button
          type="button"
          className={`${botao} border-red-200 text-red-700 hover:bg-red-50`}
          disabled={pendente("EXCLUSAO")}
          onClick={() => setPedido("EXCLUSAO")}
        >
          <Trash2 className="h-3.5 w-3.5" />
          {pendente("EXCLUSAO") ? "Pedido em análise" : "Pedir exclusão da conta"}
        </button>
      </Bloco>

      {solicitacoes.length > 0 && (
        <div className="space-y-2 border-t border-zinc-100 pt-5 dark:border-border">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-muted-foreground">Seus pedidos</p>
          <ul className="divide-y divide-zinc-100 rounded-lg border border-zinc-200 dark:divide-border dark:border-border">
            {solicitacoes.map((s) => (
              <li key={s.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
                <span className="text-zinc-900 dark:text-foreground">
                  {ROTULO_DO_TIPO_LGPD[s.tipo]} · {new Date(s.criadaEm).toLocaleDateString("pt-BR")}
                </span>
                <span className="flex items-center gap-2">
                  <span className="text-xs text-zinc-500 dark:text-muted-foreground">
                    {STATUS[s.status]}
                    {s.observacaoDoAdmin ? ` — ${s.observacaoDoAdmin}` : ""}
                  </span>
                  {s.status === "PENDENTE" && (
                    <button type="button" className="text-xs font-semibold text-brand-700 hover:underline" onClick={() => cancelar.mutate({ id: s.id })}>
                      Cancelar
                    </button>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <p className="flex items-start gap-2 text-[11.5px] text-zinc-500 dark:text-muted-foreground">
        <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        Reset e exclusão são feitos pelo suporte do DentalBook a partir do seu pedido, e você recebe um e-mail quando
        terminar. Pedidos de prótese que um laboratório já recebeu ficam com o paciente anonimizado — o laboratório
        precisa deles.
      </p>

      <PedidoLgpdDialog tipo={pedido} resumo={resumo.data} onFechar={() => setPedido(null)} onFeito={recarregar} />
    </div>
  );
}

function Bloco({ titulo, texto, children }: { titulo: string; texto: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="min-w-0 max-w-xl">
        <p className="text-sm font-semibold text-zinc-900 dark:text-foreground">{titulo}</p>
        <p className="text-[12.5px] text-zinc-500 dark:text-muted-foreground">{texto}</p>
      </div>
      {children}
    </div>
  );
}
